import React, { useState, useEffect } from 'react';
import { useMutation, gql } from '@apollo/client';
import moment from 'moment';
import {
   PostingInput,
   useMostRecentPostingsForSupplierQuery
} from '../../../generated/graphql';
import styles from './BulkUpdateSupplierPostingsModal.module.scss';
import Modal from '../../Modal/Modal';
import Button, { ButtonType } from '../../General/Button/Button';
import DatePicker from 'react-datepicker';
// import inputStyles from '../../General/FormInput/FormInput.module.scss';
// import { showSuccessToast } from '../../../utils/toastUtils';

export const CreateAndEndActivePostingMutation = gql`
   mutation CreateAndEndActivePosting($posting: PostingInput!) {
      postingCreateAndEndActive(posting: $posting) {
         ...InnerPosting
      }
   }
`

export const BulkUpdatePostingPricesMutation = gql`
   mutation BulkUpdatePostingPrices($postingPrices: [PostingPriceCreateOrUpdateInput]!) {
      postingPriceBulkCreateOrUpdate(postingPrices: $postingPrices)
   }
`



interface Props {
   supplierKey: number
   closeModal(): void
   callback?(): void
}

interface CostUpdate {
   id: number
   cost: string
   error?: string // Add error field for individual posting errors
}

const BulkUpdateSupplierPostingsModal = (props: Props) => {
   const { data, loading, error } = useMostRecentPostingsForSupplierQuery({ variables: { supplierKey: props.supplierKey } })

   const [date, setDate] = useState<Date>(new Date(moment().add(1, 'day').format('LL')))
   const [costs, setCosts] = useState<CostUpdate[]>([])
   const [submitError, setSubmitError] = useState<string>('') // Add general error state
   const [isSubmitting, setIsSubmitting] = useState<boolean>(false) // Add loading state

   const [bulkUpdatePostingPrices] = useMutation(BulkUpdatePostingPricesMutation)

   // Helper function to get the current price (matches supplier show page logic)
   const getCurrentPrice = (posting: any) => {
      return posting.currentPrice ? posting.currentPrice.cost : posting.cost
   }

   // Helper function to get the current price date (matches supplier show page logic)
   const getCurrentPriceDate = (posting: any) => {
      return posting.currentPrice ? posting.currentPrice.effectiveDate : posting.effectiveDate
   }

   // Helper function to get the future price (matches supplier show page logic)
   const getFuturePrice = (posting: any) => {
      return posting.futurePrice ? posting.futurePrice.cost : null
   }

   // Helper function to get the future price date (matches supplier show page logic)
   const getFuturePriceDate = (posting: any) => {
      return posting.futurePrice ? posting.futurePrice.effectiveDate : null
   }

   // Helper to get the default posting price (future if exists, else current)
   const getDefaultPostingPrice = (posting: any) => {
      const future = getFuturePrice(posting);
      return future !== null && future !== undefined ? future : getCurrentPrice(posting);
   }

   if (loading) return <Modal></Modal>
   if (error) return <Modal><h2>Error</h2></Modal>

   if (costs.length === 0) {
      if (data.postingsGetAllForSupplierMostRecent.length !== 0) {
         const emptyArray: CostUpdate[] = data.postingsGetAllForSupplierMostRecent.map(p => {
            const newVal: CostUpdate = { id: p.postingKey, cost: getDefaultPostingPrice(p).toString() };
            return newVal;
         });
         setCosts(emptyArray);
      }
   }

   function handleChange(i, event) {
      let newCosts: CostUpdate[] = Object.assign(costs)
      newCosts[i].cost = event.target.value
      setCosts(newCosts)
   }

   const submit = async () => {
      setIsSubmitting(true)
      setSubmitError('') // Clear previous errors

      // Clear individual posting errors
      setCosts(prevCosts => prevCosts.map(cost => ({ ...cost, error: undefined })))

      // Build the posting prices array for update
      let postingPricesForUpdate: any[] = []

      costs.forEach(c => {
         let costInt = Number(c.cost)
         if (!isNaN(costInt)) {
            let filteredPostings = data.postingsGetAllForSupplierMostRecent.filter(p => p.postingKey === c.id)
            if (filteredPostings.length) {
               let existingPosting = filteredPostings[0]
               // Get the current cost (matches supplier show page logic)
               const currentCost = getCurrentPrice(existingPosting)

               // If we are not adjusting the pricing we dont want to update the date either
               if (currentCost === costInt) {
                  return
               }               // Create PostingPriceCreateOrUpdateInput for the new price
               let postingPriceInput = {
                  postingPriceKey: null, // null for new records
                  postingKey: existingPosting.postingKey,
                  cost: costInt,
                  effectiveDate: date,
                  effectiveEndDate: new Date(2099, 11, 31) // Far future end date for database compatibility
               }

               postingPricesForUpdate.push(postingPriceInput)
            }
         }
      })

      if (postingPricesForUpdate.length === 0) {
         setIsSubmitting(false)
         setSubmitError('No valid postings to update. Please check your cost values.')
         return
      }

      try {
         // Perform the bulk update using PostingPrice system
         await performBulkUpdate(postingPricesForUpdate)
      } catch (error) {
         setIsSubmitting(false)
         console.error('Bulk update failed:', error)
         setSubmitError('An error occurred during update. Please try again.')
      }
   }

   const performBulkUpdate = async (postingPrices: any[]) => {
      try {
         // Use the new PostingPrice bulk update mutation
         const result = await bulkUpdatePostingPrices({ variables: { postingPrices } })

         if (result.data?.postingPriceBulkCreateOrUpdate) {
            // Success - reset submitting state, show success toast and close modal
            setIsSubmitting(false)
            if (props.callback) {
               await props.callback()
            }
            props.closeModal()
         } else {
            // No data returned - treat as error
            setIsSubmitting(false)
            setSubmitError('Update failed: No response from server')
         }
      } catch (error) {
         setIsSubmitting(false)

         // Don't show toast here - let the global Apollo error handler do it
         // This prevents duplicate error messages

         // For bulk update validation errors, still show individual field errors if needed
         const errorMessage = error.graphQLErrors?.[0]?.extensions?.message ||
            error.graphQLErrors?.[0]?.message ||
            error.message || 'An unexpected error occurred'

         if (errorMessage.includes('Posting ') && (errorMessage.includes('overlap') || errorMessage.includes('validation'))) {
            // Extract posting-specific errors and map them to individual postings
            const lines = errorMessage.split('\n')
            const validationErrors = lines.filter(line => line.includes('Posting '))

            // Update costs with individual errors
            setCosts(prevCosts => {
               return prevCosts.map(cost => {
                  const postingError = validationErrors.find(error => error.includes(`Posting ${cost.id}`))
                  if (postingError) {
                     // Extract just the error part after the posting info
                     const errorPart = postingError.split('): ')[1] || postingError
                     return { ...cost, error: errorPart }
                  }
                  return cost
               })
            })

            setSubmitError('Some postings have validation errors. Please review the highlighted items below.')
         } else {
            // For other errors, just show a general message in the modal (toast handles the details)
            setSubmitError('An error occurred. Please check the details and try again.')
         }
      }
   }



   const footerLeftContent = (<Button clickAction={props.closeModal} buttonType={ButtonType.Secondary}>Cancel</Button>)
   const footerRightContent = (
      <Button
         clickAction={submit}
         buttonType={ButtonType.Success}
         isDisabled={isSubmitting}
      >
         {isSubmitting ? 'Updating...' : 'Update Postings'}
      </Button>
   )


   const onDateChange = (date: Date, e) => {
      setDate(date)
   }

   const getCostForPosting = (key: number) => {
      const filteredCosts = costs.filter(c => c.id === key)
      if (filteredCosts.length) {
         return filteredCosts[0]
      }
   }

   return (
      <>
         <Modal headerText="Bulk Update Supplier Postings" footerLeftContent={footerLeftContent} footerRightContent={footerRightContent}>

            {/* Display general error message */}
            {submitError && (
               <div className={styles.errorSection}>
                  <strong>Error:</strong> {submitError}
                  {/* Show additional debug info in development */}
                  {(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && (
                     <details className={styles.debugDetails}>
                        <summary className={styles.debugSummary}>Debug Details (Development Only)</summary>
                        <pre className={styles.debugPre}>
                           Check browser console for full error details
                        </pre>
                     </details>
                  )}
               </div>
            )}

            <div className={styles.tableHeading}>
               <div className="row">
                  <div className="col-md-2">Terminal</div>
                  <div className="col-md-2">Current Posting</div>
                  <div className="col-md-2">Current Date</div>
                  <div className="col-md-2">Future Posting</div>
                  <div className="col-md-2">Future Date</div>
                  <div className="col-md-2">New Posting</div>
               </div>
            </div>

            <div className={styles.tableContainer}>
               {data.postingsGetAllForSupplierMostRecent.map((p, index) => {
                  const costUpdate = getCostForPosting(p.postingKey)
                  const hasError = costUpdate?.error

                  return (<div className={`row ${styles.tableRow} ${hasError ? styles.rowError : ''}`} key={index}>
                     <div className={`col-md-2 ${styles.tableRowColumn}`}><span className={styles.mobileOnlyLabel}>Terminal: </span>{p.terminal.name}</div>
                     <div className={`col-md-2 ${styles.tableRowColumn}`}><span className={styles.mobileOnlyLabel}>Current Posting: </span>{getCurrentPrice(p).toFixed(4)}</div>
                     <div className={`col-md-2 ${styles.tableRowColumn}`}><span className={styles.mobileOnlyLabel}>Current Date: </span>{moment.utc(getCurrentPriceDate(p)).format('MM/DD/YYYY')}</div>
                     <div className={`col-md-2 ${styles.tableRowColumn}`}><span className={styles.mobileOnlyLabel}>Future Posting: </span>{getFuturePrice(p) ? getFuturePrice(p).toFixed(4) : "-"}</div>
                     <div className={`col-md-2 ${styles.tableRowColumn}`}><span className={styles.mobileOnlyLabel}>Future Date: </span>{getFuturePriceDate(p) ? moment.utc(getFuturePriceDate(p)).format('MM/DD/YYYY') : "-"}</div>
                     <div className="col-md-2">
                        <label className={styles.mobileOnlyLabel}>New Posting</label>
                        <input
                           type="text"
                           placeholder="New Price"
                           onChange={e => handleChange(index, e)}
                           className={`form-control ${hasError ? styles.inputError : ''}`}
                           defaultValue={getDefaultPostingPrice(p).toString()}
                        />
                        {/* Display individual posting error */}
                        {hasError && (
                           <div className={styles.errorMessage}>
                              {costUpdate.error}
                           </div>
                        )}
                     </div>
                  </div>)
               })}
            </div>
            <div className="row">
               <div className="col-md-4">
                  <label className="input__wrapper">Start Date</label>
                  <DatePicker
                     className="form-control"
                     selected={date}
                     onChange={onDateChange}
                     showTimeSelect={false}
                     dateFormat="MMMM d, yyyy"
                  />
               </div>
            </div>
         </Modal>
      </>
   )
}
export default BulkUpdateSupplierPostingsModal
